import { AsyncIterableBuffer } from '../async/iterableBuffer';
import { parallelTasks } from '../async/parallelTasks';
import type { BlobView } from '../io/blobView';
import { BlobWriter } from '../io/blobWriter';
import type { Fraction } from '../util/fractionType';
import {
  divideFractionBy,
  getNumeratorForDenominator,
  multiplyFractionBy,
  subtractFractions,
} from '../util/integerMath';
import { invariant } from '../util/invariant';
import type {
  Box} from './mp4Box';
import {
  childBoxes,
  getBaseMediaDecodeTimeForMoof,
  parseTfdt,
  splitBoxes,
} from './mp4Box';

/**
 * Translate time represented by a number of segments into time represented by
 * time units used by an ISOBMFF track.
 */
function timeUnitBoundary(
  segmentDuration: Fraction,
  segmentTime: number | null,
  epsilon: Fraction,
  timeScale: number
): number | null {
  if (segmentTime == null) {
    return null;
  }

  const time = subtractFractions(
    multiplyFractionBy(segmentDuration, segmentTime),
    epsilon
  );
  return getNumeratorForDenominator(time, timeScale);
}

/**
 * Knows how to remux ISOBMFF boxes to do simple subclipping.
 */
export class SubclipMuxer {
  writer = new BlobWriter();
  timeShifts: Record<number, bigint> = {};

  /*
    TODO: At present the submixer assumes a certain time scale, frame duration
    and track layout. If needed we can make this code more generic by obtaining
    this information by parsing the ISOBMFF header boxes instead.
  */
  videoTimeScale = 12800;
  frameDuration: Fraction = { numerator: 1, denominator: 50 };
  halfFrameDuration: Fraction;
  startVtu: number | null;
  endVtu: number | null;

  constructor(
    segmentDuration: Fraction,
    start: number | null,
    end: number | null
  ) {
    this.halfFrameDuration = divideFractionBy(this.frameDuration, 2);
    this.startVtu = timeUnitBoundary(
      segmentDuration,
      start,
      this.halfFrameDuration,
      this.videoTimeScale
    );
    this.endVtu = timeUnitBoundary(
      segmentDuration,
      end,
      this.halfFrameDuration,
      this.videoTimeScale
    );
  }
  sequenceNumber = 1;
  inRange = false;

  /**
   * Either add a box to the output, or drop it, depending on whether it is
   * within the subclip time range or not.
   */
  async processBox(box: Box) {
    if (box.type == 'moof') {
      const vtu = await getBaseMediaDecodeTimeForMoof(box);
      if (vtu != null) {
        this.inRange =
          (this.startVtu == null || vtu >= this.startVtu) &&
          (this.endVtu == null || vtu < this.endVtu);
      }
    }

    if ((box.type == 'moof' || box.type == 'mdat') && !this.inRange) {
      return;
    }

    await this.writer.write(box.blob, box.size);
    if (box.type != 'moof') {
      return;
    }

    await shiftMoof(this.writer, this.timeShifts, this.sequenceNumber, box);
    ++this.sequenceNumber;
  }

  /**
   * Whether the writer currently holds buffered output not yet taken.
   */
  hasBufferedOutput(): boolean {
    return this.writer.ranges.length > 0;
  }

  /**
   * Return the byte ranges buffered so far and start a fresh writer, while
   * preserving cross-fragment timing state (timeShifts, sequenceNumber).
   *
   * Patches only ever target the moof of the current fragment, so taking the
   * buffer at fragment boundaries yields the exact same bytes as assembling the
   * whole output at once, but bounds retained memory to a single fragment.
   */
  takeBufferedOutput(): BlobView[] {
    const blobs = this.writer.getBlobSequence();
    this.writer = new BlobWriter();
    return blobs;
  }
}

/**
 * Patch an already written 'moof' box to reflect that it has a different
 * position in the output file than it has in the input file.
 */
async function shiftMoof(
  writer: BlobWriter,
  timeShifts: Record<number, bigint>,
  sequenceNumber: number,
  moof: Box
) {
  let trackId = 0;
  for await (const moofChild of childBoxes(moof)) {

    if (moofChild.type == 'mfhd') {
      /*
        From my reading of the ISOBMFF specification, the renumbering the mfhd
        sequence numbers should not be needed unless we reorder the media.

        The renumbering done here is primarily for making the output as close as
        possible to what FFmpeg produces.
      */
      await writer.patchUInt(
        moofChild.blob.offset().add(12),
        4,
        BigInt(sequenceNumber)
      );
    } else if (moofChild.type == 'traf') {
      for await (const trafChild of childBoxes(moofChild)) {
        if (trafChild.type == 'tfdt') {
          const buffer = await trafChild.blob.arrayBuffer();
          const parsed = parseTfdt(buffer);

          const time = parsed.baseMediaDecodeTime;
          let timeShift = timeShifts[trackId];
          if (timeShift == null) {
            timeShift = -time;
            timeShifts[trackId] = timeShift;
          }

          const baseMediaDecodeTime = time + timeShift;
          if (sequenceNumber == 1) {
            invariant(
              baseMediaDecodeTime == BigInt(0),
              'Base media decode time for fragment 0 should be 0n, was ' +
                baseMediaDecodeTime +
                ' due to time ' +
                time +
                ' and shift ' +
                timeShift
            );
          }

          const size = parsed.version == 0 ? 4 : 8;
          await writer.patchUInt(
            trafChild.blob.offset().add(12),
            size,
            baseMediaDecodeTime
          );
          ++trackId;
        }
      }
    }
  }
}

export interface SegmentFetcher {
  fetchSegmentTask(index: number): Promise<() => Promise<BlobView>>;

  close(): Promise<unknown>;
}

export type ProgressFunc = (part: number, whole: number) => void;


async function* fetchSegmentTasks(fetcher: SegmentFetcher, firstSegmentIndex: number, lastSegmentIndex: number, prependSegmentZero: boolean) {
  if (prependSegmentZero) {
    // Get the init segment
    yield fetcher.fetchSegmentTask(0);
  }

  for (let index = firstSegmentIndex; index <= lastSegmentIndex; ++index) {
    yield fetcher.fetchSegmentTask(index);
  }
}

export function fetchSubclipBlobs(fetcher: SegmentFetcher, firstSegmentIndex: number, lastSegmentIndex: number, prependSegmentZero: boolean): AsyncIterableIterator<BlobView> {
  /*
    When the segment sizes are small (both in terms of byte size and duration),
    it is hard to saturate available the download bandwidth, since the browser
    limits the number of parallel requests.

    We do our best here, by:
     1. Buffering fetch task creation so that we at least are not waiting for
        fetching download URLs from the server.
     2. Running upto five fetch tasks in parallel at a time.

    Pipeline:
                                           /===========\
        urls --> fetchTasks --> buffer --> parallelTasks --> boxes -> blobs
                                           \===========/
  */
  const fetchTasks = fetchSegmentTasks(fetcher, firstSegmentIndex, lastSegmentIndex, prependSegmentZero);
  const bufferedTasks = new AsyncIterableBuffer(20, fetchTasks);
  const segmentsBlobs = parallelTasks(bufferedTasks, 5);
  return segmentsBlobs;
}

/**
 * A forward-only sink for muxed output bytes. Implementations write each chunk
 * to durable storage (e.g. a File System Access writable stream) so the full
 * output is never held in memory at once.
 */
export interface ByteSink {
  write(chunk: Uint8Array): Promise<void>;
}

async function writeBlobsToSink(blobs: BlobView[], sink: ByteSink): Promise<void> {
  for (const blob of blobs) {
    const buffer = await blob.arrayBuffer();
    await sink.write(new Uint8Array(buffer));
  }
}

/**
 * Fetch, mux and stream a subclip directly to a sink, one fragment at a time.
 *
 * Unlike fetchAndMuxSubclipBlobs (which returns the whole output and therefore
 * retains every segment until assembly), this flushes each finalized fragment
 * to the sink and releases it, so peak memory is bounded to a single fragment.
 * This is what makes multi-GB downloads viable in the browser.
 */
export async function muxSubclipToStream(fetcher: SegmentFetcher, segmentDuration: Fraction,
  firstSegmentIndex: number, lastSegmentIndex: number, prependSegmentZero: boolean,
  sink: ByteSink, progress: ProgressFunc): Promise<void> {
  const segmentsBlobs = fetchSubclipBlobs(fetcher, firstSegmentIndex, lastSegmentIndex, prependSegmentZero);

  const whole = (lastSegmentIndex - firstSegmentIndex + 2);
  let part = 0;
  progress(part, whole);

  const muxer = new SubclipMuxer(segmentDuration, null, null);
  for await (const segBlob of segmentsBlobs) {
    for await (const box of splitBoxes(segBlob)) {
      // A new fragment starts at each moof; flush the previous one before it so
      // retained output never exceeds a single fragment.
      if (box.type === 'moof' && muxer.hasBufferedOutput()) {
        await writeBlobsToSink(muxer.takeBufferedOutput(), sink);
      }
      await muxer.processBox(box);
    }
    ++part;
    progress(part, whole);
  }
  await writeBlobsToSink(muxer.takeBufferedOutput(), sink);
}

export async function fetchAndMuxSubclipBlobs(fetcher: SegmentFetcher, segmentDuration: Fraction, firstSegmentIndex: number, lastSegmentIndex: number, prependSegmentZero: boolean, progress?: ProgressFunc): Promise<BlobView[]> {
  const segmentsBlobs = fetchSubclipBlobs(fetcher, firstSegmentIndex, lastSegmentIndex, prependSegmentZero);

  const whole = (lastSegmentIndex - firstSegmentIndex + 2);
  let part = 0;
  progress = progress ?? (() => {});
  progress(part, whole);

  const muxer = new SubclipMuxer(segmentDuration, null, null);
  for await (const segBlob of segmentsBlobs) {
    for await (const box of splitBoxes(segBlob)) {
      await muxer.processBox(box);
    }
    ++part;
    progress(part, whole);
  }

  return muxer.writer.getBlobSequence();
}

